import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Area from '@/components/view/layouts/area/Area'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  UGCItemRow,
  UGCProductSearchRow,
  UGCProductTagRow,
} from '@/common/model/ugcView'

import { fetchRest } from '@/common/api/fetcher'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'

const PER_PAGE_CHOICES = [24, 48, 96]

const STATUS_LABELS: Record<string, string> = {
  active: '公開中',
  pending: '承認待ち',
  rejected: '却下',
  hidden: '非表示',
}

/** 商品タグ付け統計（backend/routers/ugc_product_tags.py の get_product_tag_stats） */
interface TagStatsTopProduct {
  product_external_id: string
  tag_count: number
  name: string | null
  price: number | null
  image_url: string | null
  product_url?: string | null
}

interface TagStats {
  status: string
  total_ugc: number
  tagged_ugc: number
  untagged_ugc: number
  tag_rate: number
  top_products: TagStatsTopProduct[]
}

export default function UGCProductTagging() {
  const { siteId } = useParams()
  const [stats, setStats] = useState<TagStats | null>(null)
  const [items, setItems] = useState<UGCItemRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(24)
  // 一覧と統計は必ず同じ母集団を見る。片方だけ広いと件数が食い違う
  const [scope, setScope] = useState('active')      // active（公開中のみ）/ all
  const [taggedFilter, setTaggedFilter] = useState('') // '' / 'false'（未タグ付け）/ 'true'
  const [selectedItem, setSelectedItem] = useState<UGCItemRow | null>(null)
  const [tags, setTags] = useState<UGCProductTagRow[]>([])
  const [productSearch, setProductSearch] = useState('')
  const [productResults, setProductResults] = useState<UGCProductSearchRow[]>([])
  const [clickPos, setClickPos] = useState<{ x: number; y: number } | null>(null)
  const [showProductPicker, setShowProductPicker] = useState(false)
  const [loading, setLoading] = useState(true)
  const [broken, setBroken] = useState<Set<number>>(() => new Set())

  const fetchData = () => {
    setLoading(true)
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('per_page', String(perPage))
    if (scope === 'active') params.set('status', 'active')
    // 2026-09-29 追加：商品タグは画像上に付けるので、画像のある投稿だけを出す（統計APIも同じ母集団）
    params.set('has_media', 'true')
    if (taggedFilter) params.set('tagged', taggedFilter)

    Promise.all([
      fetchRest<TagStats>(`/api/sites/${siteId}/ugc/product-tags/stats?status=${scope}`),
      fetchRest<{ items?: UGCItemRow[]; total?: number }>(
        `/api/sites/${siteId}/ugc/items?${params.toString()}`
      ),
    ])
      .then(([s, i]) => {
        setStats(s)
        setItems(i.items || [])
        setTotal(i.total || 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchData() }, [siteId, page, perPage, scope, taggedFilter])
  // 条件を変えたら1ページ目へ戻す（3ページ目のまま絞ると空に見える）
  useEffect(() => { setPage(1) }, [scope, taggedFilter, perPage])

  const fetchTags = (itemId: number) => {
    fetchRest<{ tags?: UGCProductTagRow[] }>(`/api/sites/${siteId}/ugc/items/${itemId}/tags`)
      .then(d => setTags(d.tags || []))
      .catch(() => {})
  }

  const handleSelectItem = (item: UGCItemRow) => {
    setSelectedItem(item)
    fetchTags(item.id)
    setProductSearch('')
    setProductResults([])
    setShowProductPicker(false)
    setClickPos(null)
  }

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!selectedItem) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    // 商品検索を表示してクリック位置を保存
    setClickPos({ x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 })
    setShowProductPicker(true)
  }

  const searchProducts = (q: string) => {
    setProductSearch(q)
    if (q.length < 1) { setProductResults([]); return }
    fetchRest<{ products?: UGCProductSearchRow[] }>(
      `/api/sites/${siteId}/ugc/products/search?q=${encodeURIComponent(q)}&limit=10`
    )
      .then(d => setProductResults(d.products || []))
      .catch(() => {})
  }

  /** タグを足したら、一覧の「済み/未」表示もその場で合わせる */
  const bumpTagCount = (itemId: number, delta: number) => {
    setItems(prev => prev.map(it => (
      it.id === itemId ? { ...it, tag_count: Math.max((it.tag_count || 0) + delta, 0) } : it
    )))
  }

  const handleAddTag = (product: UGCProductSearchRow) => {
    if (!selectedItem || !clickPos) return
    fetchRest<{ created?: boolean }>(`/api/sites/${siteId}/ugc/items/${selectedItem.id}/tags`, {
      method: 'POST',
      body: JSON.stringify({
        product_external_id: product.external_id,
        hotspot_x: clickPos.x,
        hotspot_y: clickPos.y,
        tagged_by: 'manual',
      }),
    })
      .then(res => {
        fetchTags(selectedItem.id)
        setShowProductPicker(false)
        setClickPos(null)
        if (res?.created) bumpTagCount(selectedItem.id, 1)
      })
  }

  const handleDeleteTag = (tagId: number) => {
    if (!selectedItem) return
    fetchRest(`/api/sites/${siteId}/ugc/items/${selectedItem.id}/tags/${tagId}`, { method: 'DELETE' })
      .then(() => {
        fetchTags(selectedItem.id)
        bumpTagCount(selectedItem.id, -1)
      })
  }

  const totalPages = Math.max(Math.ceil(total / perPage), 1)
  const scopeLabel = scope === 'all' ? '非表示以外' : '公開中'

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🏷️ UGC 商品タグ付与'
        description='UGC画像上の位置に商品を紐づけて、ショッパブルなコンテンツを作成します'
      />

      {/* 対象の切り替え。統計・一覧・ランキングはすべてここで決まる母集団を見る */}
      <div className={`${CARD} mb-4 flex flex-wrap items-end gap-x-6 gap-y-3 px-4 py-3`}>
        <div className='flex flex-col gap-1'>
          <label className='text-secondary text-[11px] font-medium'>対象のUGC</label>
          <select value={scope} onChange={e => setScope(e.target.value)} className={`${INPUT} w-44`}>
            <option value='active'>公開中のみ</option>
            <option value='all'>非表示以外すべて（承認待ちを含む）</option>
          </select>
        </div>
        <div className='flex flex-col gap-1'>
          <label className='text-secondary text-[11px] font-medium'>タグ付け状況</label>
          <select value={taggedFilter} onChange={e => setTaggedFilter(e.target.value)} className={`${INPUT} w-40`}>
            <option value=''>すべて</option>
            <option value='false'>未タグ付けのみ</option>
            <option value='true'>タグ付け済みのみ</option>
          </select>
        </div>
        <div className='flex flex-col gap-1'>
          <label className='text-secondary text-[11px] font-medium'>1ページの表示件数</label>
          <select value={perPage} onChange={e => setPerPage(Number(e.target.value))} className={`${INPUT} w-24`}>
            {PER_PAGE_CHOICES.map(n => <option key={n} value={n}>{n}件</option>)}
          </select>
        </div>
        <p className='text-tertiary mb-1 text-[11px]'>
          統計・一覧・ランキングは、すべてここで選んだ対象で数えています。
        </p>
      </div>

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : (
        <>
          {/* 統計 */}
          <div className='mb-4 grid grid-cols-2 gap-3 md:grid-cols-4'>
            <div className={`${CARD} p-3.5 text-center`}>
              <div className='text-tertiary text-[11px]'>{scopeLabel}のUGC</div>
              <div className='text-primary text-2xl font-bold'>{stats?.total_ugc ?? 0}</div>
            </div>
            <div className={`${CARD} p-3.5 text-center`}>
              <div className='text-tertiary text-[11px]'>タグ付き</div>
              <div className='text-success text-2xl font-bold'>{stats?.tagged_ugc ?? 0}</div>
            </div>
            <div className={`${CARD} p-3.5 text-center`}>
              <div className='text-tertiary text-[11px]'>未タグ付き</div>
              <div className='text-warning text-2xl font-bold'>{stats?.untagged_ugc ?? 0}</div>
            </div>
            <div className={`${CARD} p-3.5 text-center`}>
              <div className='text-tertiary text-[11px]'>タグ付け率</div>
              <div className='text-emphasis text-2xl font-bold'>{stats?.tag_rate ?? 0}%</div>
            </div>
          </div>

          <div className={`grid gap-4 ${selectedItem ? 'lg:grid-cols-[1fr_380px]' : 'grid-cols-1'}`}>
            {/* 左: UGC一覧 */}
            <Area>
              <Heading
                subText='画像をクリックすると右側で商品を紐づけられます。左上の「🏷️ 数字」は紐づけ済みの商品の数、「未タグ付け」はまだ商品が紐づいていない画像です（画像のある投稿だけを表示しています）'
                rightNode={
                  <span className='text-secondary text-[11px]'>
                    {total.toLocaleString()}件中 {items.length ? (page - 1) * perPage + 1 : 0}〜{(page - 1) * perPage + items.length}件
                  </span>
                }
              >
                UGC一覧
              </Heading>
              <div className='border-secondary border-t px-4 py-4'>
                {items.length === 0 ? (
                  <p className='text-tertiary py-10 text-center text-xs'>
                    条件に合うUGCがありません。上の「対象のUGC」「タグ付け状況」を見直してください。
                  </p>
                ) : (
                  <div className='grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2.5'>
                    {items.map(item => {
                      const tagged = (item.tag_count || 0) > 0
                      return (
                        <div
                          key={item.id}
                          onClick={() => handleSelectItem(item)}
                          title={item.caption || ''}
                          className={`relative cursor-pointer overflow-hidden rounded-lg border-2 transition-colors ${
                            selectedItem?.id === item.id
                              ? 'border-fill-brand'
                              : tagged
                                ? 'border-success'
                                : 'border-secondary border-dashed'
                          }`}
                        >
                          {!item.media_url ? (
                            <div className='bg-surface-secondary text-tertiary flex aspect-square w-full items-center justify-center text-[10px]'>
                              テキスト
                            </div>
                          ) : broken.has(item.id) ? (
                            // 外部CDNの画像はURLが失効することがある。空の枠のままだと
                            // 「画像が無い」のか「読めなかった」のか作業者に分からない
                            <div className='bg-surface-secondary text-tertiary flex aspect-square w-full items-center justify-center px-1 text-center text-[10px]'>
                              画像を読み込めません
                            </div>
                          ) : (
                            <img
                              src={item.media_url}
                              alt=''
                              loading='lazy'
                              decoding='async'
                              onError={() => setBroken(prev => new Set(prev).add(item.id))}
                              className={`aspect-square w-full object-cover ${tagged ? '' : 'opacity-90'}`}
                            />
                          )}

                          {/* タグ付け済み/未のバッジ。作業中はここだけ見れば残りが分かる */}
                          <span
                            className={`absolute top-1 left-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                              tagged ? 'bg-fill-success text-on-bg-fill' : 'bg-surface text-secondary border border-secondary'
                            }`}
                          >
                            {tagged ? `🏷️ ${item.tag_count}` : '未タグ付け'}
                          </span>

                          {/* 公開中以外を混ぜているときは状態も出す（承認待ちに気づかず作業しないため） */}
                          {scope === 'all' && item.status !== 'active' && (
                            <span className='bg-surface/90 text-secondary absolute right-1 bottom-1 rounded px-1 py-0.5 text-[9px]'>
                              {STATUS_LABELS[item.status] || item.status}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {totalPages > 1 && (
                  <div className='mt-4 flex items-center justify-center gap-2'>
                    <Button color={ButtonColor.SECONDARY} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</Button>
                    <span className='text-secondary px-2 text-[11px]'>{page} / {totalPages}</span>
                    <Button color={ButtonColor.SECONDARY} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</Button>
                  </div>
                )}
              </div>
            </Area>

            {/* 右: タグ付けエディタ */}
            {selectedItem && (
              <Area>
                <Heading
                  rightNode={
                    <button onClick={() => { setSelectedItem(null); setTags([]) }} className='text-tertiary hover:bg-surface-hover cursor-pointer rounded p-1 text-sm'>✕</button>
                  }
                >
                  タグ付けエディタ
                </Heading>
                <div className='border-secondary border-t px-4 py-4'>
                  {/* 画像 + ホットスポット */}
                  <div className='relative mb-3 overflow-hidden rounded-lg'>
                    {selectedItem.media_url ? (
                      <img
                        src={selectedItem.media_url}
                        alt=''
                        onClick={handleImageClick}
                        className='block w-full cursor-crosshair'
                      />
                    ) : (
                      <div className='bg-surface-secondary text-tertiary flex h-[200px] w-full items-center justify-center text-xs'>
                        画像なし（テキストUGC）
                      </div>
                    )}
                    {/* ホットスポットマーカー */}
                    {tags.map(tag => (
                      tag.hotspot_x != null && tag.hotspot_y != null && (
                        <div
                          key={tag.id}
                          className='bg-fill-brand text-on-bg-fill shadow-area border-surface absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 text-[10px] font-bold'
                          style={{
                            left: `${tag.hotspot_x * 100}%`,
                            top: `${tag.hotspot_y * 100}%`,
                          }}
                          title={tag.product_name || tag.product_external_id}
                        >🏷️</div>
                      )
                    ))}
                  </div>

                  <p className='text-tertiary mb-3 text-[11px]'>
                    画像上をクリック → 商品を選択してタグ付け
                  </p>

                  {/* 商品ピッカー */}
                  {showProductPicker && (
                    <div className={`${CARD} bg-surface-secondary mb-3 p-3.5`}>
                      <div className='text-secondary mb-1.5 text-[11px] font-semibold'>
                        クリック位置: ({clickPos?.x}, {clickPos?.y}) — 紐づける商品を選択
                      </div>
                      <input
                        value={productSearch}
                        onChange={e => searchProducts(e.target.value)}
                        placeholder='商品名 or SKUで検索...'
                        className={`${INPUT} mb-2 w-full`}
                        autoFocus
                      />
                      <div className='max-h-[200px] overflow-y-auto'>
                        {productResults.map(p => (
                          <div
                            key={p.external_id}
                            onClick={() => handleAddTag(p)}
                            className='text-primary hover:bg-surface-hover flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-xs'
                          >
                            {p.image_url && <img src={p.image_url} alt='' loading='lazy' className='h-8 w-8 rounded object-cover' />}
                            <div>
                              <div className='font-semibold'>{p.name}</div>
                              <div className='text-tertiary text-[10px]'>{p.external_id} · ¥{p.price?.toLocaleString()}</div>
                            </div>
                          </div>
                        ))}
                        {productSearch && productResults.length === 0 && (
                          <p className='text-tertiary p-3 text-center text-[11px]'>該当する商品がありません</p>
                        )}
                      </div>
                      <div className='mt-1'>
                        <Button color={ButtonColor.SECONDARY} onClick={() => { setShowProductPicker(false); setClickPos(null) }}>キャンセル</Button>
                      </div>
                    </div>
                  )}

                  {/* タグ一覧 */}
                  <div>
                    <div className='text-primary mb-2 text-xs font-semibold'>紐づけ済み商品（{tags.length}）</div>
                    {tags.length === 0 ? (
                      <p className='text-tertiary text-[11px]'>タグなし。画像をクリックして商品を紐づけてください。</p>
                    ) : (
                      <div className='flex flex-col gap-1.5'>
                        {tags.map(tag => (
                          <div key={tag.id} className='bg-surface-secondary border-secondary flex items-center gap-2 rounded-lg border px-2.5 py-2'>
                            {tag.product_image_url ? (
                              <img src={tag.product_image_url} alt='' loading='lazy' className='h-9 w-9 shrink-0 rounded object-cover' />
                            ) : (
                              <span className='text-sm'>🏷️</span>
                            )}
                            <div className='min-w-0 flex-1'>
                              <div className='text-primary truncate text-xs font-semibold'>{tag.product_name || tag.product_external_id}</div>
                              <div className='text-tertiary text-[10px]'>
                                {tag.product_external_id}
                                {tag.product_price != null && <> · ¥{tag.product_price.toLocaleString()}</>}
                              </div>
                            </div>
                            <button onClick={() => handleDeleteTag(tag.id)} className='text-critical cursor-pointer text-xs'>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* UGC情報 */}
                  <div className='bg-surface-secondary text-tertiary mt-3 rounded-lg p-2.5 text-[11px]'>
                    <div>ID: #{selectedItem.id} · {selectedItem.source_type} · {selectedItem.author_handle || selectedItem.author_name || '不明'}</div>
                    {selectedItem.caption && <div className='mt-1 max-h-10 overflow-hidden'>{selectedItem.caption}</div>}
                  </div>
                </div>
              </Area>
            )}
          </div>

          {/* 商品別タグ数ランキング */}
          <div className='mt-4'>
            <Area>
              <Heading subText='どの商品にUGCが集まっているか。少ない商品ほどタグ付けの余地があります'>
                商品別タグ数ランキング
              </Heading>
              <div className='border-secondary border-t px-4 py-4'>
                {(stats?.top_products?.length ?? 0) > 0 ? (
                  <div className='grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3'>
                    {(stats?.top_products ?? []).map((p, i) => (
                      <div
                        key={p.product_external_id}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                          i < 3 ? 'border-emphasis bg-surface-emphasis' : 'border-secondary bg-surface'
                        }`}
                      >
                        <span className={`w-5 shrink-0 text-center text-sm font-bold ${i < 3 ? 'text-emphasis' : 'text-tertiary'}`}>
                          {i + 1}
                        </span>
                        {p.image_url ? (
                          <img src={p.image_url} alt='' loading='lazy' className='h-11 w-11 shrink-0 rounded object-cover' />
                        ) : (
                          <div className='bg-surface-secondary text-tertiary flex h-11 w-11 shrink-0 items-center justify-center rounded text-[9px]'>
                            画像なし
                          </div>
                        )}
                        <div className='min-w-0 flex-1'>
                          <div className='text-primary truncate text-xs font-semibold'>
                            {p.name || <span className='text-tertiary'>商品マスタに無い商品</span>}
                          </div>
                          <div className='text-tertiary truncate text-[10px]'>
                            {p.product_external_id}
                            {p.price != null && <> · ¥{p.price.toLocaleString()}</>}
                          </div>
                        </div>
                        <div className='shrink-0 text-right'>
                          <div className='text-primary text-sm font-bold'>{p.tag_count}</div>
                          <div className='text-tertiary text-[9px]'>件</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='text-tertiary py-6 text-center text-xs'>
                    まだタグ付けされた商品がありません。上のUGC一覧から画像を選んでタグ付けしてください。
                  </p>
                )}
              </div>
            </Area>
          </div>
        </>
      )}
    </Main>
  )
}
